import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PAYMENTS_ENTITLEMENTS_ME,
  PROFILE_STORES_OFFERS,
  PROFILE_STORES_OFFERS_RETENTION_BUCKETS,
  PROFILE_STORES_OFFERS_RETENTION_BUCKET_CUSTOMERS,
  PROFILE_STORES_OFFER
} from '../../../shared/constants/endpoints';
import useApiRequest from '../../../shared/hooks/useApiRequest';

const STATUS_FILTERS = [
  { key: 'all', label: 'All campaigns' },
  { key: 'live', label: 'Live' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'paused', label: 'Paused' }
];

const CHANNEL_LABELS = {
  AT_HOME: 'Home Feed',
  IN_MALL: 'In-Store',
  IN_QUEUE: 'Active Queue'
};

const OFFER_TYPE_OPTIONS = ['Standard', 'Flash', 'Upsell', 'Bundle', 'Loyalty'];
const CHANNEL_OPTIONS = ['AT_HOME', 'IN_MALL', 'IN_QUEUE'];

const DEFAULT_RETENTION_BUCKET = 'AT_RISK';

const normalizeCapabilities = (payload = {}) => {
  const source = payload?.data || payload || {};
  const capabilities = source?.capabilities || {};
  const limits = source?.limits || {};
  return {
    actualPlanName: source?.actualPlanName || '',
    effectivePlanName: source?.effectivePlanName || '',
    pilotPremiumOverrideEnabled: Boolean(source?.pilotPremiumOverrideEnabled),
    retentionBuckets: Boolean(capabilities?.retentionBuckets),
    aiOfferGeneration: Boolean(capabilities?.aiOfferGeneration),
    spotlightRecommendations: Boolean(capabilities?.spotlightRecommendations),
    advancedSegmentation: Boolean(capabilities?.advancedSegmentation),
    activeSpotlightOffers: Number(limits?.activeSpotlightOffers || 0)
  };
};

const normalizeRetentionSummary = (payload = {}) => {
  const source = payload?.data || payload || {};
  const buckets = Array.isArray(source?.buckets) ? source.buckets : [];
  return {
    expectedReturnCycleDays: Number(source?.expectedReturnCycleDays || 0),
    cycleSource: String(source?.cycleSource || ''),
    matchedCategory: String(source?.matchedCategory || ''),
    generatedAt: source?.generatedAt || null,
    totalCustomers: Number(source?.totalCustomers || 0),
    buckets: buckets.map((bucket) => ({
      bucket: String(bucket?.bucket || '').trim().toUpperCase(),
      displayName: String(bucket?.displayName || bucket?.bucket || '').trim(),
      suggestedAction: String(bucket?.suggestedAction || '').trim(),
      count: Number(bucket?.count || 0),
      percentage: Number(bucket?.percentage || 0)
    })).filter((bucket) => bucket.bucket)
  };
};

const normalizeRetentionCustomers = (payload = {}) => {
  const source = payload?.data || payload || {};
  const customers = Array.isArray(source?.customers) ? source.customers : [];
  return {
    bucket: String(source?.bucket || '').trim().toUpperCase(),
    displayName: String(source?.displayName || source?.bucket || '').trim(),
    suggestedAction: String(source?.suggestedAction || '').trim(),
    expectedReturnCycleDays: Number(source?.expectedReturnCycleDays || 0),
    totalCustomers: Number(source?.totalCustomers || 0),
    customers: customers.map((customer) => ({
      customerKey: String(customer?.customerKey || '').trim(),
      userUuid: customer?.userUuid == null ? null : String(customer.userUuid),
      name: String(customer?.name || '').trim(),
      phone: String(customer?.phone || '').trim(),
      lastVisitDate: customer?.lastVisitDate || null,
      daysSinceLastVisit: Number(customer?.daysSinceLastVisit || 0),
      expectedReturnCycleDays: Number(customer?.expectedReturnCycleDays || 0),
      visitCount: Number(customer?.visitCount || 0),
      lifetimeSpendPaise: Number(customer?.lifetimeSpendPaise || 0),
      primaryBucket: String(customer?.primaryBucket || '').trim().toUpperCase(),
      segmentTags: Array.isArray(customer?.segmentTags)
        ? customer.segmentTags.map((tag) => String(tag || '').trim().toUpperCase()).filter(Boolean)
        : [],
      suggestedAction: String(customer?.suggestedAction || '').trim()
    }))
  };
};

const normalizeRedemptionLimitType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'daily' || normalized === 'per_user') {
    return 'per_user';
  }
  if (normalized === 'total' || normalized === 'global') {
    return 'global';
  }
  return 'none';
};

const normalizeAudience = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized === 'everyone') {
    return 'all';
  }
  if (normalized === 'new_customer') {
    return 'new';
  }
  if (normalized === 'repeat_customer' || normalized === 'returning') {
    return 'repeat';
  }
  if (normalized === 'high_spender') {
    return 'vip';
  }
  return normalized;
};

const normalizeOffer = (offer = {}) => ({
  ...offer,
  id: offer?.id ?? null,
  title: String(offer?.title || '').trim(),
  type: String(offer?.type || 'Standard').trim() || 'Standard',
  lifecycle: String(offer?.lifecycle || 'paused').trim().toLowerCase(),
  statusLabel: String(offer?.statusLabel || '').trim(),
  validity: String(offer?.validity || '').trim(),
  discount: String(offer?.discount || '').trim(),
  description: String(offer?.description || '').trim(),
  appliesOnType: String(offer?.appliesOnType || 'STORE_WIDE').trim().toUpperCase() || 'STORE_WIDE',
  appliesOnCategories: String(offer?.appliesOnCategories || '').trim(),
  appliesOnProducts: String(offer?.appliesOnProducts || '').trim(),
  appliesOnMinCartValue: offer?.appliesOnMinCartValue == null ? null : Number(offer.appliesOnMinCartValue),
  impressions: Number(offer?.impressions || 0),
  targetRetentionBucket: offer?.targetRetentionBucket || null,
  redeemed: Number(offer?.redeemed || 0),
  influencedRevenue: Number(offer?.influencedRevenue || 0),
  // O5: claims = every claim made; redemptions = claims whose visit completed.
  claimsCount: Number(offer?.claimsCount ?? 0),
  redemptionsCount: Number(offer?.redemptionsCount ?? 0),
  channels: Array.isArray(offer?.channels) ? offer.channels : [],
  redemptionLimitType: normalizeRedemptionLimitType(offer?.redemptionLimitType),
  redemptionLimitValue: offer?.redemptionLimitValue == null ? null : Number(offer.redemptionLimitValue),
  couponCode: offer?.couponCode == null ? null : String(offer.couponCode).trim(),
  maxDiscountCap: offer?.maxDiscountCap == null ? null : Number(offer.maxDiscountCap),
  audience: normalizeAudience(offer?.audience),
  validDaysLabel: offer?.validDaysLabel == null ? null : String(offer.validDaysLabel).trim(),
  terms: Array.isArray(offer?.terms) ? offer.terms.map((term) => String(term)) : []
});

const normalizeOffersPayload = (responsePayload) => {
  const list = Array.isArray(responsePayload)
    ? responsePayload
    : Array.isArray(responsePayload?.offers)
      ? responsePayload.offers
      : Array.isArray(responsePayload?.content)
        ? responsePayload.content
        : Array.isArray(responsePayload?.items)
          ? responsePayload.items
          : [];
  return list.map((offer) => normalizeOffer(offer));
};

const useOffers = ({ request, storeToken, onToast } = {}) => {
  const {
    execute: executeListRequest,
    loading: listLoading,
    error: listError
  } = useApiRequest();
  const {
    execute: executeMutation,
    loading: mutationLoading,
    error: mutationError
  } = useApiRequest();
  const {
    execute: executeCapabilityRequest,
    loading: capabilityLoading,
    error: capabilityError
  } = useApiRequest();
  const {
    execute: executeRetentionSummaryRequest,
    loading: retentionSummaryLoading,
    error: retentionSummaryError
  } = useApiRequest();
  const {
    execute: executeRetentionCustomersRequest,
    loading: retentionCustomersLoading,
    error: retentionCustomersError
  } = useApiRequest();

  const [offers, setOffers] = useState([]);
  const [hasLoadedOffers, setHasLoadedOffers] = useState(false);
  const [retentionCapabilities, setRetentionCapabilities] = useState(null);
  const [hasLoadedRetentionCapabilities, setHasLoadedRetentionCapabilities] = useState(false);
  const [retentionSummary, setRetentionSummary] = useState(null);
  const [retentionCustomers, setRetentionCustomers] = useState(null);
  const [selectedRetentionBucket, setSelectedRetentionBucket] = useState(DEFAULT_RETENTION_BUCKET);
  const [filter, setFilter] = useState('all');
  const [selectedType, setSelectedType] = useState('Standard');
  const [selectedChannels, setSelectedChannels] = useState(() => new Set(['AT_HOME', 'IN_MALL']));
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;

  const sameOfferId = useCallback((left, right) => String(left) === String(right), []);

  const resolveErrorMessage = useCallback((errorValue, fallbackMessage) => (
    errorValue?.data?.message || errorValue?.message || fallbackMessage
  ), []);

  const refetchOffers = useCallback(async () => {
    if (typeof request !== 'function' || !storeToken) {
      setOffers([]);
      setHasLoadedOffers(true);
      return [];
    }

    return executeListRequest(async () => {
      const response = await request(PROFILE_STORES_OFFERS, {
        method: 'GET',
        requestName: 'offers-list'
      });
      return normalizeOffersPayload(response?.data);
    }, {
      logModule: 'useOffers.list',
      onSuccess: (nextOffers) => {
        setOffers(nextOffers);
        setHasLoadedOffers(true);
      },
      onError: (errorValue) => {
        setOffers([]);
        setHasLoadedOffers(true);
        const message = resolveErrorMessage(errorValue, 'Unable to load offers.');
        onToastRef.current?.({ message, variant: 'error' });
      }
    });
  }, [executeListRequest, request, resolveErrorMessage, storeToken]);

  useEffect(() => {
    refetchOffers().catch(() => null);
  }, [refetchOffers]);

  const refetchRetentionCapabilities = useCallback(async () => {
    if (typeof request !== 'function' || !storeToken) {
      setRetentionCapabilities(null);
      setHasLoadedRetentionCapabilities(true);
      return null;
    }

    return executeCapabilityRequest(async () => {
      const response = await request(PAYMENTS_ENTITLEMENTS_ME, {
        method: 'GET',
        requestName: 'offers-retention-entitlements'
      });
      return normalizeCapabilities(response);
    }, {
      logModule: 'useOffers.retentionCapabilities',
      onSuccess: (capabilities) => {
        setRetentionCapabilities(capabilities);
        setHasLoadedRetentionCapabilities(true);
      },
      onError: () => {
        setRetentionCapabilities(null);
        setHasLoadedRetentionCapabilities(true);
      }
    });
  }, [executeCapabilityRequest, request, storeToken]);

  const refetchRetentionSummary = useCallback(async () => {
    if (typeof request !== 'function' || !storeToken) {
      setRetentionSummary(null);
      return null;
    }

    return executeRetentionSummaryRequest(async () => {
      const response = await request(PROFILE_STORES_OFFERS_RETENTION_BUCKETS, {
        method: 'GET',
        requestName: 'offers-retention-buckets'
      });
      return normalizeRetentionSummary(response);
    }, {
      logModule: 'useOffers.retentionSummary',
      onSuccess: setRetentionSummary,
      onError: () => setRetentionSummary(null)
    });
  }, [executeRetentionSummaryRequest, request, storeToken]);

  const refetchRetentionCustomers = useCallback(async (bucket = selectedRetentionBucket) => {
    if (typeof request !== 'function' || !storeToken || !bucket) {
      setRetentionCustomers(null);
      return null;
    }

    return executeRetentionCustomersRequest(async () => {
      const response = await request(PROFILE_STORES_OFFERS_RETENTION_BUCKET_CUSTOMERS(bucket, 100), {
        method: 'GET',
        requestName: 'offers-retention-bucket-customers'
      });
      return normalizeRetentionCustomers(response);
    }, {
      logModule: 'useOffers.retentionCustomers',
      onSuccess: setRetentionCustomers,
      onError: () => setRetentionCustomers(null)
    });
  }, [executeRetentionCustomersRequest, request, selectedRetentionBucket, storeToken]);

  useEffect(() => {
    refetchRetentionCapabilities().catch(() => null);
  }, [refetchRetentionCapabilities]);

  const canUseRetentionBuckets = Boolean(retentionCapabilities?.retentionBuckets);

  useEffect(() => {
    if (!hasLoadedRetentionCapabilities || !canUseRetentionBuckets) {
      setRetentionSummary(null);
      setRetentionCustomers(null);
      return;
    }
    refetchRetentionSummary().catch(() => null);
  }, [canUseRetentionBuckets, hasLoadedRetentionCapabilities, refetchRetentionSummary]);

  useEffect(() => {
    if (!hasLoadedRetentionCapabilities || !canUseRetentionBuckets || !selectedRetentionBucket) {
      setRetentionCustomers(null);
      return;
    }
    refetchRetentionCustomers(selectedRetentionBucket).catch(() => null);
  }, [
    canUseRetentionBuckets,
    hasLoadedRetentionCapabilities,
    refetchRetentionCustomers,
    selectedRetentionBucket
  ]);

  const filteredOffers = useMemo(() => {
    if (filter === 'all') {
      return offers;
    }
    return offers.filter((offer) => offer.lifecycle === filter);
  }, [filter, offers]);

  const summary = useMemo(() => {
    const liveOffers = offers.filter((offer) => offer.lifecycle === 'live').length;
    const totalImpressions = offers.reduce((sum, offer) => sum + Number(offer.impressions || 0), 0);
    // O5: redemptions count completed visits only; claims are the funnel top.
    const totalClaims = offers.reduce((sum, offer) => sum + Number(offer.claimsCount || 0), 0);
    const totalRedemptions = offers.reduce((sum, offer) => sum + Number(offer.redemptionsCount || 0), 0);
    const influencedRevenue = offers.reduce((sum, offer) => sum + Number(offer.influencedRevenue || 0), 0);
    const redemptionRate = totalImpressions > 0
      ? ((totalRedemptions / totalImpressions) * 100).toFixed(1)
      : '0.0';
    return {
      liveOffers,
      totalImpressions,
      totalClaims,
      totalRedemptions,
      influencedRevenue,
      redemptionRate
    };
  }, [offers]);

  const createOffer = async (payload) => {
    return executeMutation(async () => {
      if (typeof request !== 'function' || !storeToken) {
        throw new Error('Missing authenticated request context');
      }

      const response = await request(PROFILE_STORES_OFFERS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'offers-create',
        body: JSON.stringify(payload)
      });
      const created = normalizeOffer(response?.data);
      setOffers((previous) => [created, ...previous]);
      return created;
    }, {
      logModule: 'useOffers.create',
      onSuccess: () => onToastRef.current?.({ message: 'Offer saved.', variant: 'success' }),
      onError: (error) => {
        const message = resolveErrorMessage(error, 'Unable to save offer.');
        onToastRef.current?.({ message, variant: 'error' });
      }
    });
  };

  const updateOffer = async (offerId, updates) => {
    return executeMutation(async () => {
      if (typeof request !== 'function' || !storeToken) {
        throw new Error('Missing authenticated request context');
      }

      const response = await request(PROFILE_STORES_OFFER(offerId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        requestName: 'offers-update',
        body: JSON.stringify(updates)
      });
      const updated = normalizeOffer(response?.data);
      setOffers((previous) => previous.map((offer) => (
        sameOfferId(offer.id, offerId) ? { ...offer, ...updated } : offer
      )));
      return updated;
    }, {
      logModule: 'useOffers.update',
      onSuccess: () => onToastRef.current?.({ message: 'Offer updated.', variant: 'success' }),
      onError: (error) => {
        const message = resolveErrorMessage(error, 'Unable to update offer.');
        onToastRef.current?.({ message, variant: 'error' });
      }
    });
  };

  const toggleOfferLifecycle = async (offer) => {
    const nextLifecycle = offer?.lifecycle === 'paused' ? 'live' : 'paused';
    return updateOffer(offer?.id, { lifecycle: nextLifecycle });
  };

  const removeOffer = async (offerId) => {
    return executeMutation(async () => {
      if (typeof request !== 'function' || !storeToken) {
        throw new Error('Missing authenticated request context');
      }
      await request(PROFILE_STORES_OFFER(offerId), {
        method: 'DELETE',
        requestName: 'offers-delete'
      });
      setOffers((previous) => previous.filter((offer) => !sameOfferId(offer.id, offerId)));
      return offerId;
    }, {
      logModule: 'useOffers.remove',
      onSuccess: () => onToastRef.current?.({ message: 'Offer removed.', variant: 'success' }),
      onError: (error) => {
        const message = resolveErrorMessage(error, 'Unable to remove offer.');
        onToastRef.current?.({ message, variant: 'error' });
      }
    });
  };

  const toggleChannel = (channel) => {
    setSelectedChannels((previous) => {
      const next = new Set(previous);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  };

  return {
    offers,
    filter,
    setFilter,
    filteredOffers,
    summary,
    selectedType,
    setSelectedType,
    selectedChannels,
    setSelectedChannels,
    toggleChannel,
    createOffer,
    updateOffer,
    toggleOfferLifecycle,
    removeOffer,
    refetchOffers,
    retentionCapabilities,
    canUseRetentionBuckets,
    hasLoadedRetentionCapabilities,
    retentionSummary,
    retentionCustomers,
    selectedRetentionBucket,
    setSelectedRetentionBucket,
    refetchRetentionCapabilities,
    refetchRetentionSummary,
    refetchRetentionCustomers,
    retentionLoading: capabilityLoading || retentionSummaryLoading || retentionCustomersLoading,
    retentionSummaryLoading,
    retentionCustomersLoading,
    retentionError: capabilityError || retentionSummaryError || retentionCustomersError,
    isMutating: mutationLoading,
    isInitialLoading: listLoading && !hasLoadedOffers,
    loading: listLoading || mutationLoading,
    error: listError || mutationError,
    statusFilters: STATUS_FILTERS,
    channelLabels: CHANNEL_LABELS,
    offerTypeOptions: OFFER_TYPE_OPTIONS,
    channelOptions: CHANNEL_OPTIONS,
  };
};

export default useOffers;
